import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
import { MilestonePill } from '../components/MilestonePill';
import { loans } from '../data/portal';
type Tab = 'all' | 'in-process' | 'closed';
interface MyFilesProps {
  onOpenLoan: (id: string) => void;
}
const tabs: {
  id: Tab;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'in-process',
  label: 'In process'
}, {
  id: 'closed',
  label: 'Closed'
}];
export function MyFiles({
  onOpenLoan
}: MyFilesProps) {
  const [tab, setTab] = useState<Tab>('all');
  const rows = tab === 'all' ? loans : loans.filter(l => l.status === tab);
  const count = (t: Tab) => t === 'all' ? loans.length : loans.filter(l => l.status === t).length;
  return <div className="mx-auto max-w-[1400px]" data-id="element-213" data-mp="wdjmk1cgefe1">
      <div className="flex items-end justify-between" data-id="element-214" data-mp="1k579k86lh6b1">
        <div data-id="element-215" data-mp="1k3m9k31igv5s">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-id="element-216" data-mp="1ovxbfbftar0z">My Files</h1>
          <p className="mt-1 text-sm text-slate-500" data-id="element-217" data-mp="1qd277y8h4enz">Every buyer you’ve referred, with where their loan stands today.</p>
        </div>
      </div>

      <div role="tablist" aria-label="Filter files" className="mt-8 flex gap-6 border-b border-slate-200" data-id="element-218" data-mp="1mcs0gjcuxk82">
        {tabs.map(t => {
        const active = t.id === tab;
        return <button key={t.id} role="tab" aria-selected={active} onClick={() => setTab(t.id)} className={`relative -mb-px flex items-center gap-2 border-b-2 pb-3 text-sm transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out focus:outline-none focus-visible:text-slate-900 ${active ? 'border-transparent font-semibold text-slate-900' : 'border-transparent font-medium text-slate-500 hover:text-slate-800'}`} data-id="element-219" data-mp="1vb3xb34bhe8y">
              {t.label}
              <span className={`rounded-full px-2 py-0.5 text-xs tabular-nums transition-colors duration-200 ${active ? 'bg-ink text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-220" data-mp="jqge7g3wsr3w">
                {count(t.id)}
              </span>
              {active && <motion.span layoutId="files-tab-underline" aria-hidden="true" className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-brand" transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-221" data-mp="2trf78zuh8k4o" />}
            </button>;
      })}
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-id="element-222" data-mp="3cglmgcw2atlq">
        <table className="w-full text-sm" data-id="element-223" data-mp="3hdlpsg1j1i95">
          <thead data-id="element-224" data-mp="3hgpztu1ob87q">
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500" data-id="element-225" data-mp="3lg8o8zspe1fb">
              <th className="px-6 py-3 font-medium" data-id="element-226" data-mp="3qfbidp75an8i">Buyer</th>
              <th className="px-6 py-3 font-medium" data-id="element-227" data-mp="3qfl7zboa2sdo">Property</th>
              <th className="px-6 py-3 font-medium" data-id="element-228" data-mp="1es87x0ou6yrs">Milestone</th>
              <th className="px-6 py-3 font-medium" data-id="element-229" data-mp="19svbppc2sfcj">{tab === 'closed' ? 'Closed' : 'Est. closing'}</th>
              <th className="px-6 py-3 font-medium" data-id="element-230" data-mp="15ra5unxyl1bu">Loan officer</th>
              <th className="px-6 py-3 font-medium" data-id="element-231" data-mp="15rjv29b6j4gh">Last update</th>
              <th className="w-10 px-6 py-3" aria-hidden="true" data-id="element-232" data-mp="10t8ktxgz2rxq" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-233" data-mp="1xhel686v2y67">
            {rows.map((l, i) => <tr key={`${tab}-${l.id}`} style={{
            '--d': `${Math.min(i, 8) * 35}ms`
          } as React.CSSProperties} onClick={() => onOpenLoan(l.id)} onKeyDown={e => e.key === 'Enter' && onOpenLoan(l.id)} tabIndex={0} className="group animate-fade-up cursor-pointer transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" data-id="element-234" data-mp="1thxdr97vjqye" data-mpi={i}>
                <td className="whitespace-nowrap px-6 py-4 font-semibold text-slate-900" data-id="element-235" data-mp="1vyo3h8dxcw89">{l.buyer}</td>
                <td className="whitespace-nowrap px-6 py-4 text-slate-600" data-id="element-236" data-mp="210vjqa5seyge">
                  {l.street}, {l.city}
                </td>
                <td className="px-6 py-4" data-id="element-237" data-mp="1rymcpu9q9n7r">
                  <MilestonePill milestone={l.milestone} data-id="element-238" data-mp="1rznpq4ab0js4" />
                </td>
                <td className="whitespace-nowrap px-6 py-4 tabular-nums text-slate-900" data-id="element-239" data-mp="2fnotkzwjfc17">{l.estCloseShort}</td>
                <td className="whitespace-nowrap px-6 py-4 text-slate-600" data-id="element-240" data-mp="2joqrndepxi1l">{l.loanOfficer}</td>
                <td className="whitespace-nowrap px-6 py-4 tabular-nums text-slate-500" data-id="element-241" data-mp="25z6tylzzvaz5">{l.lastUpdate}</td>
                <td className="px-6 py-4 text-slate-300 transition-colors duration-200 group-hover:text-slate-500" data-id="element-242" data-mp="2awghtx7qjekv">
                  <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-243" data-mp="2ylhujwnlpbvy" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}